import type { ProductReview, ProductReviewVote } from '~/types/products'
import { defineStore } from 'pinia'
import { useNuxtApp } from '#app'
import { baseStore } from '../Base'
import type { LlanaListResponse, LlanaWhere, LlanaSocketData } from '~/types/llana'

function defaultList(): LlanaListResponse<ProductReview> {
	return {
		data: [],
		total: 0,
		limit: default_limit,
		offset: 0,
	}
}

const table_name = 'product_reviews'
type T = ProductReview
const default_sort = 'votes.desc'
const default_limit = 20
const default_relations = 'product,users,product_reviews_upvotes,product_reviews_downvotes'

export class ProductReviewsStore extends baseStore<T> {
	constructor(product_id: number) {
		const store_name = `product_${product_id}_reviews`
		super({
			table_name,
			store_name,
			default_sort,
			default_limit,
			default_relations,
		})
	}
}

export const useProductReviewsStore = (product_id: number) =>
	defineStore(`product_${product_id}_reviews`, {
		state: () => <LlanaListResponse<T>>defaultList(),
		actions: {
			async bindEvents(where?: { [key: string]: string | number }) {
				const { $llanaSubscribe } = useNuxtApp()
				await $llanaSubscribe(
					table_name,
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.remove(data.id as number, where),
				)
			},
			async create(data: Partial<T>) {
				const store = new ProductReviewsStore(product_id)
				const { state, record } = await store.create(this.$state, data)
				this.$state = state
				return record
			},
			async get(id: number, where?: { [key: string]: string | number }): Promise<T | undefined> {
				const store = new ProductReviewsStore(product_id)
				const { state, record } = await store.get(this.$state, id, where)
				this.$state = state
				return record
			},
			async list(options?: {
				force?: boolean
				limit?: number
				offset?: number
				where?: LlanaWhere[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<LlanaListResponse<T>> {
				const store = new ProductReviewsStore(product_id)
				this.$state = await store.list(this.$state, options)
				return this.$state
			},
			async search(options?: {
				limit?: number
				offset?: number
				where?: LlanaWhere[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<LlanaListResponse<T>> {
				const store = new ProductReviewsStore(product_id)
				return await store.search(options)
			},
			async reload(): Promise<LlanaListResponse<T>> {
				const store = new ProductReviewsStore(product_id)
				this.$state = await store.reload()
				return this.$state
			},
			async update(id: number, data: Partial<T>): Promise<T> {
				const store = new ProductReviewsStore(product_id)
				const { state, record } = await store.update(this.$state, id, data)
				this.$state = state
				return record
			},
			async remove(id: number, where?: { [key: string]: string | number }): Promise<void> {
				const store = new ProductReviewsStore(product_id)
				this.$state = await store.remove(this.$state, id, where)
			},
			async delete(id: number): Promise<void> {
				const store = new ProductReviewsStore(product_id)
				this.$state = await store.delete(this.$state, id, true)
			},

			/**
			 * STORE SPECIFIC ACTIONS
			 * * Upvote a review
			 * * Remove an upvote
			 * * Downvote a review
			 * * Remove a downvote
			 **/

			async upvote(data: { product_review_id: number, user_id: number }): Promise<void> {
										const { $llana } = useNuxtApp()
										const upvote = await $llana<ProductReviewVote>({
											type: 'CREATE',
											table: 'product_reviews_upvotes',
											data,
										})

										let review_index = this.$state.data.findIndex((p: T) => p.id === data.product_review_id)

										if(review_index === -1) {
											this.$state.data.push(await this.get(data.product_review_id) as T)
											review_index = this.$state.data.findIndex((p: T) => p.id === data.product_review_id)
										}

										this.$state.data[review_index].product_reviews_upvotes.push(upvote as ProductReviewVote)
										this.$state.data[review_index].votes += 1
									},

									async removeUpvote(options: { product_review_id: number, user_id: number }): Promise<void> {
										let review_index = this.$state.data.findIndex((p: T) => p.id === options.product_review_id)

										if(review_index === -1) {
											this.$state.data.push(await this.get(options.product_review_id))
											review_index = this.$state.data.findIndex((p: T) => p.id === options.product_review_id)
										}

										const vote_index = this.$state.data[review_index].product_reviews_upvotes?.findIndex(
											(v: ProductReviewVote) => v.product_review_id === options.product_review_id && v.user_id === options.user_id,
										)

										if (vote_index > -1) {
											const { $llana } = useNuxtApp()
											await $llana<ProductReviewVote>({
												type: 'DELETE',
												table: 'product_reviews_upvotes',
												id: this.$state.data[review_index].product_reviews_upvotes[vote_index].id,
												hard: true,
											})

											this.$state.data[review_index].product_reviews_upvotes.splice(vote_index, 1)
											this.$state.data[review_index].votes -= 1
										}
									},

									async downvote(data: { product_review_id: number, user_id: number }): Promise<void> {
										const { $llana } = useNuxtApp()
										const downvote = await $llana<ProductReviewVote>({
											type: 'CREATE',
											table: 'product_reviews_downvotes',
											data,
										})

										let review_index = this.$state.data.findIndex((p: T) => p.id === data.product_review_id)

										if(review_index === -1) {
											this.$state.data.push(await this.get(data.product_review_id))
											review_index = this.$state.data.findIndex((p: T) => p.id === data.product_review_id)
										}

										const product_reviews_downvotes = this.$state.data[review_index].product_reviews_downvotes
										product_reviews_downvotes.push(downvote as ProductReviewVote)
										const votes = this.$state.data[review_index].votes - 1

										const article: T = {
											...this.$state.data[review_index],
											product_reviews_downvotes,
											votes,
										}
										this.$state.data[review_index] = article
									},

									async removeDownvote(options: { product_review_id: number, user_id: number }): Promise<void> {

										let review_index = this.$state.data.findIndex((p: T) => p.id === options.product_review_id)

										if(review_index === -1) {
											this.$state.data.push(await this.get(options.product_review_id))
											review_index = this.$state.data.findIndex((p: T) => p.id === options.product_review_id)
										}

										const vote_index = this.$state.data[review_index].product_reviews_downvotes?.findIndex(
											(v: ProductReviewVote) => v.product_review_id === options.product_review_id && v.user_id === options.user_id,
										)

										if (vote_index > -1) {
											const { $llana } = useNuxtApp()
											await $llana<ProductReviewVote>({
												type: 'DELETE',
												table: 'product_reviews_downvotes',
												id: this.$state.data[review_index].product_reviews_downvotes[vote_index].id,
												hard: true,
											})

											const product_reviews_downvotes = this.$state.data[review_index].product_reviews_downvotes
											product_reviews_downvotes.splice(vote_index, 1)
											const votes = this.$state.data[review_index].votes + 1

											const article: T = {
												...this.$state.data[review_index],
												product_reviews_downvotes,
												votes,
											}

											this.$state.data[review_index] = article
										}
									},
		},
	})
